import { Controller } from '@hotwired/stimulus';
import TomSelect from 'tom-select';

import 'tom-select/dist/css/tom-select.css';

// Tom Select never renders the original <option> — it draws its own markup for
// both the open menu and the chosen item — so an attribute such as `title` set
// on the <option> is left behind on the hidden <select> and never surfaces.
// Its data-* attributes DO survive (init_select reads the whole dataset into
// the option's data), which is why the hint travels as data-tooltip and is put
// back on as a native title here. Same function for both templates so the hint
// stays readable once the menu is closed. Default markup otherwise, so a field
// that sets no tooltip renders exactly as before.
const renderWithTooltip = (data, escape) => {
  const label = escape(data.text ?? '');

  if (!data.tooltip) return `<div>${label}</div>`;

  return `<div title="${escape(data.tooltip)}">${label}</div>`;
};

// Attach to any <select data-controller="searchable-select"> to turn a long
// native option list into a type-to-filter dropdown, styled to match the
// parent field (see .select-wrapper .ts-wrapper / .ts-dropdown in form.scss).
export default class extends Controller {
  // shared/fields/_select.html.erb and _collection_select.html.erb both hand
  // down the "choose a value" hint (e.g. "Please select") as this value
  // rather than as a real, labelled blank <option> (see their comments) — it
  // was never declared here, so this.placeholderValue always read as
  // undefined below and Tom Select showed no hint text at all once the
  // labelless blank option got stripped, leaving the field looking empty.
  static values = { placeholder: String, autoselect: Boolean };

  initialize() {
    // Turbo snapshots the page for its cache before Stimulus disconnects
    // controllers, so a select tom-select already enhanced gets cached with
    // its "tomselected"/"ts-hidden-accessible" classes still on it. Restoring
    // that snapshot later then re-enhances an already-marked <select>, corrupting
    // the new instance. Tearing down before the snapshot is taken keeps it clean.
    this.teardownForCache = () => this.tomSelect?.destroy();
    document.addEventListener('turbo:before-cache', this.teardownForCache);
  }

  connect() {
    // The root Vue instance in app.js mounts on #page on every 'turbo:load' by
    // recompiling the LIVE DOM as its template source (see the comment further
    // below) — which reads outerHTML, i.e. attributes, never live DOM properties.
    // Tom Select conflicts with that in two ways once a <select> is re-enhanced
    // after such a remount:
    //  1. Its own updateOriginalInput() (see AddSelected in
    //     node_modules/tom-select/src/tom-select.ts) moves the currently selected
    //     <option>(s) to the end of the underlying <select> on every sync — inert
    //     on a normal page, but baked in permanently once a second connect() scans
    //     that already-reordered <select> as if it were the original order.
    //  2. It marks the chosen <option> via the live `selected` DOM property only,
    //     never the "selected" HTML attribute — invisible to outerHTML, so a value
    //     picked through Tom Select would silently revert to whichever option the
    //     server originally marked selected.
    // Snapshotting the pristine markup on the first connect and replaying it
    // (restoring whatever value is currently active, and mirroring that onto the
    // "selected" attribute) on every later one keeps both order and selection
    // stable no matter how many times Tom Select or Vue touch the element.
    const syncSelectedAttributes = () => {
      Array.from(this.element.options).forEach((option) => {
        option.toggleAttribute('selected', option.selected);
      });
    };

    const currentValue = this.element.value;
    if (this.element.dataset.searchableSelectPristineHtml === undefined) {
      this.element.dataset.searchableSelectPristineHtml = this.element.innerHTML;
    } else {
      this.element.innerHTML = this.element.dataset.searchableSelectPristineHtml;
      this.element.value = currentValue;
      syncSelectedAttributes();
    }

    // If a remount happens after this controller already connected once, it
    // re-fires connect() on the field without ever calling disconnect() on the
    // previous instance first — leaving both the old (leftover) .ts-wrapper
    // sibling AND its "tomselected"/"ts-hidden-accessible" markers behind, so
    // the next connect() both duplicates the visible control and corrupts the
    // new instance's wrapper (it copies those stale classes at creation).
    // Removing any leftover wrapper and stripping the markers first makes
    // connect() idempotent regardless of how many times it ends up firing.
    const staleWrapper = this.element.nextElementSibling;
    if (staleWrapper?.classList.contains('ts-wrapper')) staleWrapper.remove();
    this.element.classList.remove('tomselected', 'ts-hidden-accessible');
    this.tomSelect = new TomSelect(this.element, {
      maxOptions: null,
      allowEmptyOption: true,
      dropdownParent: 'body',
      placeholder: this.placeholderValue || undefined,
      render: { option: renderWithTooltip, item: renderWithTooltip },
    });

    // The empty state is carried by an unlabelled blank <option> (see the
    // partials above). Left in place it renders as a blank row in the open
    // dropdown and, being "selected", suppresses the placeholder — so drop it
    // from the list and empty the control, which is what makes the placeholder
    // visible and the field readable as "nothing chosen yet". A blank option
    // that does carry a label (e.g. a caller-supplied include_blank string
    // such as "Please select", or "None") is a real, selectable choice — it
    // stays, and clearing the control here would hide it despite it being the
    // one actually selected, blanking a field that has a perfectly good
    // labelled default to show. Only clear when a blank option was actually
    // stripped, so the field falls back to the placeholder instead.
    const blankOption = Object.values(this.tomSelect.options).find((option) => {
      const label = option[this.tomSelect.settings.labelField];
      return !option[this.tomSelect.settings.valueField] && !String(label ?? '').trim();
    });

    if (blankOption) {
      // clear() BEFORE removeOption(), not after: clear() is the only one of the
      // two that runs inputState(), and inputState() is what takes the
      // "input-hidden" class off the wrapper — the class tom-select sets while
      // an item is showing, which parks the control's own <input> at
      // `left: -10000px; opacity: 0` (its own CSS). That input carries the
      // placeholder AND is the node focus() targets, so leaving the class on
      // costs two visible things: the field renders empty instead of showing
      // its "Select ..." hint, and the first click lands on an off-screen input
      // rather than opening the menu (the resulting focus runs inputState(),
      // which is why the second click works). Ordered the other way round,
      // removeOption() removes the blank's item too, so clear() hits its
      // `if (!self.items.length) return` and inputState() never runs.
      if (!this.element.value) this.tomSelect.clear(true);

      // Silently: removeOption() -> removeItem() -> updateOriginalInput()
      // dispatches a native "change" on the <select> unless told not to, and
      // in the consumption modals that change is what drives the cascade
      // (activity -> type -> fuel -> unit) by re-rendering the whole
      // turbo-frame — whose freshly connected controller removes the option
      // again, dispatching another change, for an endless re-render loop that
      // tears the control down and rebuilds it under the user's cursor.
      this.tomSelect.removeOption(blankOption[this.tomSelect.settings.valueField], true);
    }

    this.element.addEventListener('change', syncSelectedAttributes);

    // A field offering a single value asks a question with only one answer:
    // pick it, and let the resulting change event drive the dependent fields
    // (activity -> type -> fuel -> unit) exactly as a manual pick would. This
    // is the one place the event is wanted, so setValue() is deliberately NOT
    // silent — and it cannot loop the way the blank-option removal did, because
    // the field comes back from the re-render with a value, so the guard below
    // is false the second time round.
    //
    // Skipped when the blank option carries a real label ("None", "Not
    // selected", ...): there the empty choice is a legitimate answer, and
    // auto-selecting the only other option would make it unreachable — picking
    // the blank would immediately re-select that single value again.
    const labelledBlank = Object.values(this.tomSelect.options).some((option) => {
      const label = option[this.tomSelect.settings.labelField];
      return !option[this.tomSelect.settings.valueField] && String(label ?? '').trim();
    });
    if (this.autoselectValue && !this.element.value && !labelledBlank) {
      const [only, ...rest] = Object.keys(this.tomSelect.options).filter((value) => value !== '');
      if (only !== undefined && rest.length === 0) this.tomSelect.setValue(only);
    }

    // The currently selected option is rendered as its own "item" element
    // alongside the search input, not as text inside it — so merely
    // resetting the input's text (see git history) left the old item visible
    // above whatever the user typed next. Clearing the value itself on focus
    // removes that item outright; if the user then clicks away without
    // picking a new option, the previous value is restored silently so
    // nothing is actually lost.
    this.clearSelectionOnFocus = () => {
      const previousValue = this.tomSelect.getValue();
      if (!previousValue) return;
      this.selectionBeforeSearch = previousValue;
      this.tomSelect.clear(true);
    };
    this.restoreSelectionOnBlur = () => {
      if (this.selectionBeforeSearch && !this.tomSelect.getValue()) {
        this.tomSelect.setValue(this.selectionBeforeSearch, true);
      }
      this.selectionBeforeSearch = null;
    };
    this.tomSelect.control_input.addEventListener('focus', this.clearSelectionOnFocus);
    this.tomSelect.on('blur', this.restoreSelectionOnBlur);

    // Clearing the value on focus also costs Tom Select its only clue to which
    // option to highlight: refreshOptions() falls back to the first selectable
    // one when nothing is selected, so the open menu always started from the
    // top instead of from the value the field was showing. Until the user types
    // a query, put the highlight (and the scroll position) back on the option
    // that was selected before the search began. Hooked after refreshOptions()
    // rather than on 'dropdown_open', because open() fires that event before
    // refreshOptions() applies its own (first-option) highlight.
    this.tomSelect.hook('after', 'refreshOptions', () => {
      if (!this.tomSelect.isOpen) return;

      // Typing a query can shrink (or grow back) the menu without Tom Select
      // repositioning it, which would leave a menu opened above the field
      // floating away from it — re-anchor it to the new height.
      this.tomSelect.positionDropdown();

      if (!this.selectionBeforeSearch) return;
      if (this.tomSelect.inputValue()) return;

      const option = this.tomSelect.getOption(this.selectionBeforeSearch);
      if (!option) return;

      this.tomSelect.setActiveOption(option, false);

      // scrollToOption() only scrolls as far as needed to bring the option into
      // view, which leaves it at the bottom edge of the menu — scroll it to the
      // top instead, so the list visibly starts from the current value. Through
      // scroll(..., 'auto') rather than setting scrollTop directly: tom-select's
      // CSS gives .ts-dropdown-content `scroll-behavior: smooth`, which would
      // animate the menu from its first option down to this one on every open.
      const content = this.tomSelect.dropdown_content;
      const offset = option.getBoundingClientRect().top - content.getBoundingClientRect().top;
      this.tomSelect.scroll(content.scrollTop + offset, 'auto');
    });

    // positionDropdown() always drops the menu below the field, so a field in
    // the lower half of the screen (e.g. Subtype in the procurement modal) got
    // a menu cut off by the bottom of the viewport, often hiding the very
    // option that was highlighted. Flip it above the field instead in that
    // case. Tom Select's own CSS spaces the menu from the field with a top
    // margin, which would push a flipped menu down onto the field, so the gap
    // is subtracted here instead.
    this.tomSelect.hook('after', 'positionDropdown', () => {
      const { control, dropdown } = this.tomSelect;
      const rect = control.getBoundingClientRect();
      const openAbove = rect.top + rect.height / 2 > window.innerHeight / 2;

      dropdown.style.marginTop = openAbove ? '0' : '';
      if (!openAbove) return;

      const gap = 4;
      dropdown.style.top = `${rect.top + window.scrollY - dropdown.offsetHeight - gap}px`;
    });

    // Vue-driven fields (e.g. select_filter_transport.vue) repopulate a
    // select's <option>s in place in response to a sibling field changing,
    // which Tom Select has no way to notice on its own — dispatching this
    // event after such an update re-reads the live <option>s/value.
    this.syncFromElement = () => this.tomSelect?.sync();
    this.element.addEventListener('searchable-select:sync', this.syncFromElement);
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.teardownForCache);
    this.element.removeEventListener('searchable-select:sync', this.syncFromElement);
    if (this.clearSelectionOnFocus) {
      this.tomSelect?.control_input?.removeEventListener('focus', this.clearSelectionOnFocus);
      this.tomSelect?.off('blur', this.restoreSelectionOnBlur);
    }
    this.tomSelect?.destroy();
  }
}
